import { useState, useEffect, useRef, useMemo } from 'react';
import { AppHeader, Button, Dialog, Notice, TextInput, RichEditor, SortableList, SortableItem, useSortable } from '@origo/origo-ui';
import { FloppyDiskIcon, PlusIcon } from '@phosphor-icons/react';
import {
    useSeksjonelementer,
    useAddSeksjonelement,
    useUpdateSeksjonelement,
    useDeleteSeksjonelement,
    useReorderSeksjonelementer,
} from '@origo/seksjon';
import type { Seksjon, SeksjonFormValues } from '../types';
import { SectionPreview } from './SectionPreview';
import type { SectionElement, GalleryItem } from './Gallery/types';
import { GallerySelectionModal } from './Gallery/GallerySelectionModal';
import { ArcGisImage } from './Gallery/ArcGisImage';
import styles from './SectionEditorPage.module.css';
import { sanitizeForEditor, restorePlaceholderBrackets } from './renderHtml';
import { resolveGalleryUri, buildImageUrl } from './Gallery/resolveGalleryUri';
const KOMMUNALT_BILDE_PREFIX = '__kommunalt_bilde:';

interface SectionEditorPageProps {
    open: boolean;
    /** Definert = redigeringsmodus, undefined = ny seksjon */
    seksjon?: Seksjon;
    contextLabel: string;
    onSave: (values: SeksjonFormValues) => Promise<string | undefined>;
    onClose: () => void;
    /** Planen er publisert — lagring skal holde planen publisert. */
    isPublished?: boolean;
    /** Kalles etter vellykket lagring når planen er publisert, for å re-publisere. */
    onPublishAfterSave?: () => Promise<void>;
}

interface FormState {
    tittel: string;
    tekst: string;
    kommunal: boolean;
    /** 3-tilstand: undefined = ikke satt (default-topp for kommunale), true = topp, false = bunn. */
    topp: boolean | undefined;
    infoboks: boolean;
}

function emptyForm(): FormState {
    return { tittel: '', tekst: '', kommunal: false, topp: undefined, infoboks: false };
}

function formFromSeksjon(s: Seksjon): FormState {
    return {
        tittel: s.tittel,
        tekst: sanitizeForEditor(s.tekst),
        kommunal: s.kommunal,
        topp: s.topp,
        infoboks: Boolean(s.infoboks),
    };
}

function checkUnsavedChanges(form: FormState, original: FormState | null, pendingCount: number): boolean {
    if (!original) return form.tittel !== '' || form.tekst !== '' || pendingCount > 0;
    return (
        form.tittel !== original.tittel ||
        form.tekst !== original.tekst ||
        form.kommunal !== original.kommunal ||
        form.topp !== original.topp ||
        form.infoboks !== original.infoboks
    );
}

export function SectionEditorPage({
    open,
    seksjon,
    contextLabel,
    onSave,
    onClose,
    isPublished,
    onPublishAfterSave,
}: SectionEditorPageProps) {
    const dialogRef = useRef<HTMLDialogElement>(null);
    const titleInputRef = useRef<HTMLInputElement>(null);
    const [form, setForm] = useState<FormState>(emptyForm);
    const [original, setOriginal] = useState<FormState | null>(null);
    const [showConfirm, setShowConfirm] = useState(false);
    const [galleryOpen, setGalleryOpen] = useState(false);
    const [editorKey, setEditorKey] = useState(0);
    const [isSaving, setIsSaving] = useState(false);
    const [saveError, setSaveError] = useState<string | null>(null);
    const [editingElement, setEditingElement] = useState<{ index: number; title: string; description: string; imageUrl: string; isPending: boolean; kommunaltBilde: boolean } | null>(null);
    // Gallery items queued before the section has been saved (no globalId yet)
    const [pendingElements, setPendingElements] = useState<GalleryItem[]>([]);
    // Local draft of elements for existing sections — null = no local changes yet
    const [localElements, setLocalElements] = useState<SectionElement[] | null>(null);

    const isEdit = seksjon !== undefined;
    const hasGlobalId = Boolean(seksjon?.globalId);

    // Element-henting via TanStack Query. Aktiv kun når editoren er åpen og
    // seksjonen har blitt lagret (globalId). Lokalt view-shape bygges fra
    // domain SectionElement — URL-en er miljø-spesifikk og bor her.
    const elementsQuery = useSeksjonelementer(
        open && hasGlobalId ? seksjon!.globalId! : undefined,
    );
    const elements = useMemo<SectionElement[]>(
        () =>
            (elementsQuery.data ?? []).map((el) => {
                const rawBeskrivelse = el.beskrivelse ?? '';
                let imageUrl = '';
                let description = rawBeskrivelse;
                let _persistUri: string | undefined;

                if (rawBeskrivelse.startsWith(KOMMUNALT_BILDE_PREFIX)) {
                    const rest = rawBeskrivelse.slice(KOMMUNALT_BILDE_PREFIX.length);
                    const nl = rest.indexOf('\n');
                    _persistUri = nl >= 0 ? rest.slice(0, nl) : rest;
                    imageUrl = _persistUri;
                    description = nl >= 0 ? rest.slice(nl + 1) : '';
                } else {
                    const filIdStr = el.filId != null ? String(el.filId) : '';
                    imageUrl = buildImageUrl(filIdStr);
                }

                return {
                    id: el.objectId,
                    fil_id: Number(el.filId ?? 0),
                    title: el.tittel ?? '',
                    imageUrl,
                    description,
                    prioritet: el.prioritet,
                    SeksjonsGlobalID: seksjon?.globalId,
                    _persistUri,
                };
            }),
        [elementsQuery.data, seksjon?.globalId],
    );

    const addElementMutation = useAddSeksjonelement();
    const updateElementMutation = useUpdateSeksjonelement();
    const deleteElementMutation = useDeleteSeksjonelement();
    const reorderElementsMutation = useReorderSeksjonelementer();

    // Sync dialog open/close
    useEffect(() => {
        const el = dialogRef.current;
        if (!el) return;
        if (open) {
            if (!el.open) el.showModal();
        } else {
            if (el.open) el.close();
        }
    }, [open]);

    // Prevent ESC from closing without dirty check
    useEffect(() => {
        const el = dialogRef.current;
        if (!el) return;
        function handleCancel(e: Event) {
            e.preventDefault();
            handleAvbryt();
        }
        el.addEventListener('cancel', handleCancel);
        return () => el.removeEventListener('cancel', handleCancel);
    });

    // Reset form when editor opens + send focus to the primary task (Tittel)
    useEffect(() => {
        if (open) {
            const initial = seksjon ? formFromSeksjon(seksjon) : emptyForm();
            setForm(initial);
            setOriginal(seksjon ? initial : null);
            setShowConfirm(false);
            setSaveError(null);
            setIsSaving(false);
            setPendingElements([]);
            setLocalElements(null);
            setEditorKey(k => k + 1);
            requestAnimationFrame(() => titleInputRef.current?.focus());
        }
    }, [open, seksjon]);

    const hasUnsavedChanges = useMemo(
        () => checkUnsavedChanges(form, original, pendingElements.length) || (hasGlobalId && localElements !== null),
        [form, original, pendingElements.length, hasGlobalId, localElements],
    );

    function handleAvbryt() {
        if (hasUnsavedChanges) {
            setShowConfirm(true);
        } else {
            onClose();
        }
    }

    async function handleSave() {
        if (!form.tittel.trim() || isSaving) return;
        setIsSaving(true);
        setSaveError(null);
        try {
            const globalId = await onSave({
                tittel: form.tittel,
                tekst: restorePlaceholderBrackets(form.tekst),
                kommunal: form.kommunal,
                topp: form.topp,
                infoboks: form.infoboks,
            });
            // Add any gallery items that were queued before the section existed
            if (globalId && pendingElements.length > 0) {
                for (let i = 0; i < pendingElements.length; i++) {
                    const item = pendingElements[i];
                    const isKommunalt = item.source === 'kommunalt';
                    const beskrivelse = isKommunalt && item.persistUri
                        ? (`${KOMMUNALT_BILDE_PREFIX}${item.persistUri}\n${item.beskrivelse ?? ''}`).trimEnd() || undefined
                        : (item.beskrivelse ?? undefined);
                    await addElementMutation.mutateAsync({
                        seksjonsGlobalId: globalId,
                        filId: isKommunalt ? undefined : (item.filId ? String(item.filId) : undefined),
                        tittel: item.tittel,
                        beskrivelse,
                        prioritet: i,
                    });
                }
            }
            // Apply queued element changes for existing sections
            if (hasGlobalId && localElements !== null && seksjon?.globalId) {
                const existingGlobalId = seksjon.globalId;
                const localIds = new Set(localElements.filter(el => el.id != null).map(el => el.id!));
                for (const el of elements) {
                    if (el.id != null && !localIds.has(el.id)) {
                        await deleteElementMutation.mutateAsync({
                            objectId: el.id,
                            seksjonGlobalId: existingGlobalId,
                        });
                    }
                }
                const serverById = new Map(elements.filter(e => e.id != null).map(e => [e.id!, e]));
                const ordered: { objectId: number; prioritet: number }[] = [];
                for (let i = 0; i < localElements.length; i++) {
                    const el = localElements[i];
                    if (el.id == null) {
                        const beskrivelse = el._persistUri
                            ? (`${KOMMUNALT_BILDE_PREFIX}${el._persistUri}\n${el.description}`).trimEnd() || undefined
                            : (el.description || undefined);
                        await addElementMutation.mutateAsync({
                            seksjonsGlobalId: existingGlobalId,
                            filId: el._persistUri ? undefined : (el.fil_id ? String(el.fil_id) : undefined),
                            tittel: el.title,
                            beskrivelse,
                            prioritet: i,
                        });
                    } else {
                        ordered.push({ objectId: el.id, prioritet: i });
                        const server = serverById.get(el.id);
                        if (server && (server.title !== el.title || server.description !== el.description)) {
                            const beskrivelse = el._persistUri
                                ? (`${KOMMUNALT_BILDE_PREFIX}${el._persistUri}\n${el.description}`).trimEnd() || undefined
                                : (el.description || undefined);
                            await updateElementMutation.mutateAsync({
                                objectId: el.id,
                                tittel: el.title,
                                beskrivelse,
                                seksjonGlobalId: existingGlobalId,
                            });
                        }
                    }
                }
                if (ordered.length > 0) {
                    await reorderElementsMutation.mutateAsync({
                        seksjonGlobalId: existingGlobalId,
                        ordered,
                    });
                }
            }
            if (isPublished) await onPublishAfterSave?.();
            onClose();
        } catch (err) {
            const msg = err instanceof Error ? err.message : 'Ukjent feil ved lagring';
            setSaveError(msg);
        } finally {
            setIsSaving(false);
        }
    }

    async function handleAddFromGallery(items: GalleryItem[]) {
        if (hasGlobalId) {
            // Queue locally — will be persisted when user clicks Lagre
            const base = localElements ?? sortedElements;
            const newEls: SectionElement[] = items.map((item, i) => ({
                id: undefined,
                fil_id: item.filId ?? 0,
                title: item.tittel,
                imageUrl: resolveGalleryUri(item.uri) ?? buildImageUrl(item.filId),
                description: item.beskrivelse ?? '',
                prioritet: base.length + i,
                SeksjonsGlobalID: seksjon?.globalId,
                _persistUri: item.persistUri ?? undefined,
            }));
            setLocalElements([...base, ...newEls]);
        } else {
            // New section — queue items; they'll be saved after the section is created
            setPendingElements(prev => [...prev, ...items]);
        }
        setGalleryOpen(false);
    }

    function handleRemoveElement(index: number) {
        setLocalElements(prev => (prev ?? sortedElements).filter((_, i) => i !== index));
    }

    function handleRemovePending(index: number) {
        setPendingElements(prev => prev.filter((_, i) => i !== index));
    }

    function handleEditElement(index: number, isPending: boolean) {
        const title = isPending
            ? (pendingElements[index]?.tittel ?? '')
            : (displayElements[index]?.title ?? '');
        const description = isPending
            ? (pendingElements[index]?.beskrivelse ?? '')
            : (displayElements[index]?.description ?? '');
        const imageUrl = isPending
            ? (resolveGalleryUri(pendingElements[index]?.uri) ?? buildImageUrl(pendingElements[index]?.filId))
            : (displayElements[index]?.imageUrl ?? '');
        const kommunaltBilde = isPending
            ? (pendingElements[index]?.source === 'kommunalt')
            : !!displayElements[index]?._persistUri;
        setEditingElement({ index, title, description, imageUrl, isPending, kommunaltBilde });
    }

    function handleSaveElementDescription() {
        if (!editingElement) return;
        const { index, title, description, isPending } = editingElement;
        if (isPending) {
            setPendingElements(prev => prev.map((el, i) => i === index ? { ...el, tittel: title, beskrivelse: description } : el));
        } else {
            setLocalElements(prev => (prev ?? sortedElements).map((el, i) => i === index ? { ...el, title, description } : el));
        }
        setEditingElement(null);
    }

    const sortedElements = useMemo(
        () => [...elements].sort((a, b) => a.prioritet - b.prioritet),
        [elements],
    );

    const displayElements = localElements ?? sortedElements;

    const sortable = useSortable({
        count: displayElements.length,
        onReorder: (fromIndex, toIndex) => {
            const reordered = [...displayElements];
            const [moved] = reordered.splice(fromIndex, 1);
            reordered.splice(toIndex, 0, moved);
            setLocalElements(reordered.map((el, i) => ({ ...el, prioritet: i })));
        },
    });

    // Elements shown in preview — local draft for existing sections, pending for new
    const previewElements: SectionElement[] = hasGlobalId
        ? displayElements
        : pendingElements.map((item, i) => ({
              id: undefined,
              fil_id: item.filId,
              title: item.tittel,
              imageUrl: resolveGalleryUri(item.uri) ?? buildImageUrl(item.filId),
              description: item.beskrivelse ?? null,
              prioritet: i,
          }));

    return (
        <dialog ref={dialogRef} className={styles.page}>
            <AppHeader
                title={isEdit ? 'Rediger veiledningspunkt' : 'Nytt veiledningspunkt'}
                onBack={handleAvbryt}
                backLabel={contextLabel}
                actions={
                    <Button
                        onClick={handleSave}
                        disabled={!form.tittel.trim() || isSaving}
                        leftIcon={<FloppyDiskIcon size={16} />}
                    >
                        {isSaving
                            ? (isPublished ? 'Publiserer…' : 'Lagrer…')
                            : (isPublished ? 'Publiser' : 'Lagre')}
                    </Button>
                }
            />

            <div className={styles.body}>
                <div className={styles.formPanel}>
                    {saveError && (
                        <Notice variant="danger" onDismiss={() => setSaveError(null)}>
                            {saveError}
                        </Notice>
                    )}
                    <TextInput
                        ref={titleInputRef}
                        label="Tittel"
                        value={form.tittel}
                        onChange={e => {
                            if (saveError) setSaveError(null);
                            setForm(f => ({ ...f, tittel: e.target.value }));
                        }}
                        placeholder="Skriv inn tittel…"
                        required
                        aria-required="true"
                    />
                    <RichEditor
                        key={editorKey}
                        label="Detaljert beskrivelse"
                        value={form.tekst}
                        onChange={tekst => {
                            if (saveError) setSaveError(null);
                            setForm(f => ({ ...f, tekst }));
                        }}
                        placeholder="Skriv veiledningstekst…"
                    />
                    <div className={styles.illustrasjonerSection}>
                        <p className={styles.illustrasjonerHeading}>Illustrasjoner</p>
                        {hasGlobalId ? (
                            <>
                                {displayElements.length > 0 && (
                                    <SortableList>
                                        {displayElements.map((el, i) => {
                                            const itemProps = sortable.getItemProps(i);
                                            return (
                                                <div key={el.id ?? i} ref={itemProps.ref} style={itemProps.style}>
                                                    <SortableItem
                                                        index={i + 1}
                                                        title={el.title}
                                                        icon={el.imageUrl ? (
                                                            !!el._persistUri
                                                                ? <ArcGisImage src={el.imageUrl} alt={el.title} style={{ width: 40, height: 40, objectFit: 'cover', borderRadius: 'var(--radius-sm)' }} />
                                                                : <img src={el.imageUrl} alt={el.title} style={{ width: 40, height: 40, objectFit: 'cover', borderRadius: 'var(--radius-sm)' }} />
                                                        ) : undefined}
                                                        dragHandleProps={sortable.getDragHandleProps(i)}
                                                        isDragging={sortable.state.dragIndex === i}
                                                        actions={
                                                            <>
                                                                <Button variant="ghost" size="sm" onClick={() => handleEditElement(i, false)}>Rediger</Button>
                                                                <Button variant="ghost" size="sm" onClick={() => handleRemoveElement(i)}>Fjern</Button>
                                                            </>
                                                        }
                                                    />
                                                </div>
                                            );
                                        })}
                                    </SortableList>
                                )}
                                <div>
                                    <Button variant="secondary" size="sm" onClick={() => setGalleryOpen(true)}>
                                        <PlusIcon size={14} style={{ marginRight: 4 }} />
                                        Legg til fra galleri
                                    </Button>
                                </div>
                            </>
                        ) : (
                            <>
                                {pendingElements.length > 0 && (
                                    <SortableList>
                                        {pendingElements.map((item, i) => (
                                            <div key={`pending-${i}`}>
                                                <SortableItem
                                                    index={i + 1}
                                                    title={item.tittel}
                                                    icon={(item.uri || item.filId) ? (
                                                        <img src={resolveGalleryUri(item.uri) ?? buildImageUrl(item.filId) ?? undefined} alt={item.tittel} style={{ width: 40, height: 40, objectFit: 'cover', borderRadius: 'var(--radius-sm)' }} />
                                                    ) : undefined}
                                                    actions={
                                                        <>
                                                            <Button variant="ghost" size="sm" onClick={() => handleEditElement(i, true)}>Rediger</Button>
                                                            <Button variant="ghost" size="sm" onClick={() => handleRemovePending(i)}>Fjern</Button>
                                                        </>
                                                    }
                                                />
                                            </div>
                                        ))}
                                    </SortableList>
                                )}
                                <div>
                                    <Button variant="secondary" size="sm" onClick={() => setGalleryOpen(true)}>
                                        <PlusIcon size={14} style={{ marginRight: 4 }} />
                                        Legg til fra galleri
                                    </Button>
                                </div>
                            </>
                        )}
                    </div>
                </div>

                <div className={styles.previewPanel}>
                    <p className={styles.previewHeading}>Forhåndsvisning</p>
                    <SectionPreview
                        form={form}
                        elements={previewElements.length > 0 ? previewElements : undefined}
                    />
                </div>
            </div>

            {/* Avbryt-bekreftelse */}
            <Dialog
                open={showConfirm}
                onClose={() => setShowConfirm(false)}
                title="Forkast endringer?"
                footer={
                    <>
                        <Button variant="secondary" onClick={() => setShowConfirm(false)}>
                            Fortsett redigering
                        </Button>
                        <Button variant="danger" onClick={() => { setShowConfirm(false); onClose(); }}>
                            Forkast endringer
                        </Button>
                    </>
                }
            >
                <p style={{ margin: 0 }}>
                    Du har ulagrede endringer. Hvis du avbryter nå, vil endringene gå tapt.
                </p>
            </Dialog>

            <Dialog
                open={editingElement !== null}
                onClose={() => setEditingElement(null)}
                title="Rediger tekst"
                className={styles.wideDialog}
                footer={
                    <>
                        <Button variant="secondary" onClick={() => setEditingElement(null)}>Avbryt</Button>
                        <Button onClick={handleSaveElementDescription}>Lagre</Button>
                    </>
                }
            >
                {editingElement !== null && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-05)' }}>
                        <TextInput
                            label="Tittel"
                            value={editingElement.title}
                            onChange={e => setEditingElement(prev => prev ? { ...prev, title: e.target.value } : prev)}
                            placeholder="Skriv tittel…"
                        />
                        <RichEditor
                            label="Beskrivelse"
                            value={editingElement.description}
                            onChange={description => setEditingElement(prev => prev ? { ...prev, description } : prev)}
                            placeholder="Skriv beskrivelse…"
                        />
                        {editingElement.imageUrl && (
                            editingElement.kommunaltBilde
                                ? <ArcGisImage src={editingElement.imageUrl} alt={editingElement.title} style={{ width: '100%', borderRadius: 'var(--radius-md)', objectFit: 'cover' }} />
                                : <img src={editingElement.imageUrl} alt={editingElement.title} style={{ width: '100%', borderRadius: 'var(--radius-md)', objectFit: 'cover' }} />
                        )}
                    </div>
                )}
            </Dialog>

            <GallerySelectionModal
                open={galleryOpen}
                onClose={() => setGalleryOpen(false)}
                onSelectItems={handleAddFromGallery}
            />
        </dialog>
    );
}
